/**
 * Colour-scheme resolution: which of light / dark a visitor sees.
 *
 * Precedence, highest first:
 *   1. The visitor's own choice (the header toggle), stored per tenant under
 *      `<siteId>-theme`. Never overridden by a site default.
 *   2. The site's live default, `site_settings.theme_config.color_scheme_default`
 *      ('light' | 'dark' | 'system'), applied by RuntimeBrand when settings
 *      arrive. A dark-first template (Nightfall) sets this.
 *   3. The build-time baseline, `theme.colorSchemeDefault` in site.config.ts
 *      (SITE_COLOR_SCHEME_DEFAULT), so a dark-first tenant does not paint light
 *      while the settings request is in flight.
 *   4. 'light'.
 *
 * `system` resolves through `prefers-color-scheme`. The scheme that was
 * actually applied is cached under COLOR_SCHEME_CACHE_KEY so
 * public/theme-boot.js can put `.dark` on <html> before first paint on the next
 * load — the same trick the theme-color meta uses.
 */

export type ColorScheme = 'light' | 'dark';
export type ColorSchemeDefault = ColorScheme | 'system';

/**
 * localStorage key the pre-paint boot script reads. MUST stay in sync with the
 * literal in web/public/theme-boot.js (a static asset that can't import this).
 * Not namespaced by SITE_ID on purpose: localStorage is origin-scoped and every
 * prod tenant is its own domain.
 */
export const COLOR_SCHEME_CACHE_KEY = 'rumio:color-scheme';

/** Window event RuntimeBrand dispatches when the site default changes the applied scheme. */
export const COLOR_SCHEME_EVENT = 'rumio:color-scheme';

export function isColorScheme(value: unknown): value is ColorScheme {
  return value === 'light' || value === 'dark';
}

export function isColorSchemeDefault(value: unknown): value is ColorSchemeDefault {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function systemPrefersDark(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  } catch {
    return false;
  }
}

/** Turn a default ('system' included) into the concrete scheme to apply. */
export function resolveSchemeDefault(value: unknown, prefersDark = systemPrefersDark()): ColorScheme {
  if (value === 'dark') return 'dark';
  if (value === 'system') return prefersDark ? 'dark' : 'light';
  return 'light';
}

/**
 * The scheme to apply given the visitor's stored choice (if any) and the best
 * site default known at the time.
 */
export function resolveInitialScheme(
  stored: unknown,
  siteDefault: unknown,
  prefersDark = systemPrefersDark(),
): ColorScheme {
  if (isColorScheme(stored)) return stored;
  return resolveSchemeDefault(siteDefault, prefersDark);
}

export function readCachedScheme(): ColorScheme | null {
  try {
    const v = localStorage.getItem(COLOR_SCHEME_CACHE_KEY);
    return isColorScheme(v) ? v : null;
  } catch {
    return null;
  }
}

/** Cache the applied scheme for the boot script; swallows storage errors. */
export function cacheColorScheme(scheme: ColorScheme): void {
  try {
    localStorage.setItem(COLOR_SCHEME_CACHE_KEY, scheme);
  } catch {
    /* storage unavailable — boot script keeps the baked value */
  }
}

/** Put the scheme on <html> and cache it. */
export function applyColorScheme(scheme: ColorScheme): void {
  document.documentElement.classList.toggle('dark', scheme === 'dark');
  cacheColorScheme(scheme);
}
